import React from 'react';
import { PreProcessingReport } from './ImportOrdersSection';

export interface ImportResult {
  ordersCreated: number;
  guestsCreated: number;
  ordersSkipped: number;
  warnings: string[];
}

interface PreProcessingSummaryProps {
  report: PreProcessingReport;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming: boolean;
  result: ImportResult | null;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.25rem',
  },
  heading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginTop: 0,
    marginBottom: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  field: {
    fontSize: '0.85rem',
    color: '#374151',
  },
  label: {
    fontWeight: 600,
    color: '#6b7280',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    marginBottom: '0.15rem',
  },
  value: {
    fontWeight: 500,
    color: '#111827',
  },
  duplicateSection: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  duplicateTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.5rem',
  },
  duplicateList: {
    fontSize: '0.8rem',
    color: '#6b7280',
    margin: 0,
    paddingLeft: '1.25rem',
  },
  noDuplicates: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  buttonRow: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.25rem',
  },
  confirmButton: {
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#1a6b3c',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  confirmButtonDisabled: {
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#9ca3af',
    border: 'none',
    borderRadius: '6px',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  cancelButtonDisabled: {
    padding: '0.5rem 1.25rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#9ca3af',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'not-allowed',
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  resultSuccess: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#dcfce7',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#166534',
  },
  resultSummary: {
    fontWeight: 600,
    marginBottom: '0.5rem',
  },
  resultDetail: {
    marginBottom: '0.25rem',
  },
  warningSection: {
    marginTop: '0.75rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fef9c3',
    border: '1px solid #fde047',
    borderRadius: '4px',
  },
  warningTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#854d0e',
    marginBottom: '0.25rem',
  },
  warningList: {
    margin: 0,
    paddingLeft: '1.25rem',
    fontSize: '0.8rem',
    color: '#854d0e',
  },
};

export function PreProcessingSummary({
  report,
  onConfirm,
  onCancel,
  isConfirming,
  result,
}: PreProcessingSummaryProps) {
  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Pre-Processing Summary</h4>

      <div style={styles.grid}>
        <div style={styles.field}>
          <div style={styles.label}>File Name</div>
          <div style={styles.value}>{report.fileName}</div>
        </div>
        <div style={styles.field}>
          <div style={styles.label}>File Reference ID</div>
          <div style={styles.value}>{report.fileReferenceId}</div>
        </div>
        <div style={styles.field}>
          <div style={styles.label}>Upload Date</div>
          <div style={styles.value}>{report.uploadDate}</div>
        </div>
        <div style={styles.field}>
          <div style={styles.label}>Uploaded By</div>
          <div style={styles.value}>{report.uploadedByName}</div>
        </div>
        <div style={styles.field}>
          <div style={styles.label}>Total Guests</div>
          <div style={styles.value}>{report.guestCount}</div>
        </div>
        <div style={styles.field}>
          <div style={styles.label}>Total Orders</div>
          <div style={styles.value}>{report.orderCount}</div>
        </div>
      </div>

      <div style={styles.duplicateSection}>
        <div style={styles.duplicateTitle}>
          Duplicates: {report.duplicateCount}
        </div>
        {report.duplicateCount > 0 ? (
          <ul style={styles.duplicateList}>
            {report.duplicateReferenceIds.map((refId) => (
              <li key={refId}>{refId}</li>
            ))}
          </ul>
        ) : (
          <div style={styles.noDuplicates}>0 duplicates — no matching Reference IDs found</div>
        )}
      </div>

      {/* Post-confirmation result */}
      {result && (
        <div style={styles.resultSuccess}>
          <div style={styles.resultSummary}>Import completed successfully!</div>
          <div style={styles.resultDetail}>Orders created: {result.ordersCreated}</div>
          <div style={styles.resultDetail}>Guests created: {result.guestsCreated}</div>
          <div style={styles.resultDetail}>Orders skipped: {result.ordersSkipped}</div>
          {result.warnings.length > 0 && (
            <div style={styles.warningSection}>
              <div style={styles.warningTitle}>Warnings ({result.warnings.length})</div>
              <ul style={styles.warningList}>
                {result.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Loading indicator during confirmation */}
      {isConfirming && (
        <div style={styles.loadingIndicator}>
          <span>⏳</span>
          <span>Processing import... please wait</span>
        </div>
      )}

      {/* Action buttons — hide after result is available */}
      {!result && (
        <div style={styles.buttonRow}>
          <button
            style={isConfirming ? styles.confirmButtonDisabled : styles.confirmButton}
            onClick={onConfirm}
            disabled={isConfirming}
          >
            Confirm Import
          </button>
          <button
            style={isConfirming ? styles.cancelButtonDisabled : styles.cancelButton}
            onClick={onCancel}
            disabled={isConfirming}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
